import React, { useState, useEffect, useCallback } from 'react';
import {
    Bot, Calendar, Clock, MapPin, Phone, User, ArrowUpDown, Loader2,
    RefreshCw, ChevronDown, PhoneOutgoing, Settings, Plus, CheckCircle,
    Volume2,
} from 'lucide-react';
import {
    fetchZoomBotAppointments,
    formatPhoneNumber,
    getEventTypeInfo,
    getVoicemailAudioUrl,
    getStatusColor,
    COMMUNICATION_CHAIN_STATUS,
} from '../services/communicationCenterService';

const SORT_OPTIONS = {
    APPOINTMENT_DATE: 'appointment_date',
    DATE_CREATED: 'date_created',
};

const getEventsNewestFirst = (events = []) => [...events].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

/**
 * Parse structured appointment details from the booking event notes.
 * The notes follow the format written by ReceptionBot:
 *   📅 March 20, 2026 at 10:00 AM
 *   ✂️ Groomer: Jane
 *   🐕 Pet: Buddy
 *   💇 Service: Full Groom (60 min)
 *   💰 Price: $75.00
 *   🔖 Confirmation: #12345
 */
const parseBookingNotes = (notes) => {
    if (!notes) return {};

    const parsed = {};
    const lines = notes.split('\n');

    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('📅')) {
            parsed.appointmentDateStr = trimmed.replace('📅', '').trim();
        } else if (trimmed.startsWith('✂️')) {
            parsed.groomer = trimmed.replace('✂️', '').replace('Groomer:', '').trim();
        } else if (trimmed.startsWith('🐕')) {
            parsed.pet = trimmed.replace('🐕', '').replace('Pet:', '').trim();
        } else if (trimmed.startsWith('💇')) {
            parsed.service = trimmed.replace('💇', '').replace('Service:', '').trim();
        } else if (trimmed.startsWith('💰')) {
            parsed.price = trimmed.replace('💰', '').replace('Price:', '').trim();
        } else if (trimmed.startsWith('🔖')) {
            parsed.confirmation = trimmed.replace('🔖', '').replace('Confirmation:', '').replace('#', '').trim();
        }
    }

    return parsed;
};

/**
 * Try to parse the appointment date string into a Date object.
 * Handles formats like "March 20, 2026 at 10:00 AM"
 */
const parseAppointmentDate = (dateStr) => {
    if (!dateStr) return null;
    const cleaned = dateStr.replace(' at ', ' ');
    const d = new Date(cleaned);
    return isNaN(d.getTime()) ? null : d;
};

/** Inline audio player for call recordings / voicemails */
const AudioPlayer = ({ blobName, bucket, label }) => {
    const [audioUrl, setAudioUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handlePlay = async () => {
        if (audioUrl) return;
        setLoading(true);
        setError(null);
        try {
            const url = await getVoicemailAudioUrl(blobName, bucket);
            setAudioUrl(url);
        } catch (err) {
            setError('Failed to load audio');
        } finally {
            setLoading(false);
        }
    };

    if (!blobName) return null;

    return (
        <div className="mt-1">
            {!audioUrl && !loading && !error && (
                <button
                    onClick={handlePlay}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                    <Volume2 className="w-3.5 h-3.5" />
                    {label || 'Play voicemail'}
                </button>
            )}
            {loading && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading audio...
                </span>
            )}
            {error && <span className="text-xs text-red-500">{error}</span>}
            {audioUrl && (
                <audio controls autoPlay className="h-8 w-full max-w-xs">
                    <source src={audioUrl} type="audio/wav" />
                </audio>
            )}
        </div>
    );
};

/**
 * ZoomBot Appointments Panel
 *
 * Props:
 *   locationId          - Filter by location (null = all)
 *   onCallClient        - (phone, clientContext) => void
 *   onOpenChainDetail   - (chainLikeObj) => void  (settings gear)
 *   onAddEvent          - (chainId) => void
 *   onMarkResolved      - (chainId) => void
 *   onUpdateStatus      - (chainId, status) => void
 *   userIdToFullName    - { userId: "Full Name" } map
 *   formatTimestamp      - (isoStr) => display string
 */
const ZoomBotAppointmentsPanel = ({
    locationId,
    onCallClient,
    onOpenChainDetail,
    onAddEvent,
    onMarkResolved,
    onUpdateStatus,
    userIdToFullName = {},
    formatTimestamp,
}) => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState(SORT_OPTIONS.APPOINTMENT_DATE);
    const [expandedCard, setExpandedCard] = useState(null);

    const loadAppointments = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchZoomBotAppointments({
                locationId: locationId || undefined,
            });
            setAppointments(result.appointments || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [locationId]);

    useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    // Enrich appointments with parsed data and sort
    const enrichedAppointments = appointments.map((appt) => {
        const parsed = parseBookingNotes(appt.booking_notes);
        const appointmentDate = parseAppointmentDate(parsed.appointmentDateStr);
        return { ...appt, parsed, appointmentDate };
    });

    const sortedAppointments = [...enrichedAppointments].sort((a, b) => {
        if (sortBy === SORT_OPTIONS.APPOINTMENT_DATE) {
            const dateA = a.appointmentDate ? a.appointmentDate.getTime() : 0;
            const dateB = b.appointmentDate ? b.appointmentDate.getTime() : 0;
            return dateB - dateA;
        }
        const createdA = a.booking_created_at || '';
        const createdB = b.booking_created_at || '';
        return createdB.localeCompare(createdA);
    });

    const getLocationName = (id) => {
        if (id === 1) return 'Hopkins';
        if (id === 2) return 'Inver Grove';
        return 'Unknown';
    };

    const formatCreatedDate = (isoStr) => {
        if (!isoStr) return '—';
        const d = new Date(isoStr);
        return d.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true,
        });
    };

    const formatApptDate = (date) => {
        if (!date) return '—';
        return date.toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true,
        });
    };

    const fmtTs = (ts) => {
        if (formatTimestamp) return formatTimestamp(ts);
        return formatCreatedDate(ts);
    };

    const isUpcoming = (date) => date && date > new Date();

    const toggleExpand = (key) => {
        setExpandedCard(expandedCard === key ? null : key);
    };

    // Build a chain-like object so onOpenChainDetail (ChainDetailModal) can consume it
    const toChainObj = (appt) => ({
        chain_id: appt.chain_id,
        client_name: appt.client_name,
        client_phone: appt.client_phone,
        client_id: appt.client_id,
        client_email: appt.client_email,
        location_id: appt.location_id,
        status: appt.chain_status,
        notes: appt.chain_notes,
        events: appt.events || [],
        created_at: appt.chain_created_at,
        updated_at: appt.chain_updated_at,
    });

    return (
        <div>
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Bot className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-semibold text-gray-800">
                        Zoom Bot Appointments
                    </h2>
                    {!loading && (
                        <span className="ml-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                            {sortedAppointments.length}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 bg-white border rounded-lg px-2 py-1.5">
                        <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="text-sm text-gray-600 bg-transparent border-none outline-none cursor-pointer pr-1"
                        >
                            <option value={SORT_OPTIONS.APPOINTMENT_DATE}>Appointment Date</option>
                            <option value={SORT_OPTIONS.DATE_CREATED}>Date Created</option>
                        </select>
                    </div>
                    <button
                        onClick={loadAppointments}
                        disabled={loading}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    <span className="ml-2 text-sm text-gray-500">Loading Zoom Bot appointments…</span>
                </div>
            ) : sortedAppointments.length === 0 ? (
                <div className="text-center py-16">
                    <Bot className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No appointments booked by Zoom Bot yet.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {sortedAppointments.map((appt, idx) => {
                        const { parsed, appointmentDate } = appt;
                        const upcoming = isUpcoming(appointmentDate);
                        const cardKey = `${appt.chain_id}-${idx}`;
                        const isExpanded = expandedCard === cardKey;

                        return (
                            <div
                                key={cardKey}
                                className={`bg-white rounded-xl border overflow-hidden transition-shadow hover:shadow-md ${
                                    upcoming ? 'border-emerald-200' : 'border-gray-200'
                                }`}
                            >
                                {/* Card body */}
                                <div className="p-4">
                                    {/* Top row: client info + action buttons + location */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                                upcoming ? 'bg-emerald-100' : 'bg-gray-100'
                                            }`}>
                                                <Bot className={`w-4 h-4 ${upcoming ? 'text-emerald-600' : 'text-gray-400'}`} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-800 text-sm truncate">
                                                    {appt.client_name || 'Unknown Client'}
                                                    {appt.client_id && (
                                                        <a
                                                            href={`https://cloud.daysmartpet.com/beta/Client/${appt.client_id}/history/upcoming`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="ml-2 text-xs text-blue-600 hover:text-blue-800 hover:underline font-normal"
                                                        >
                                                            View in DaySmart →
                                                        </a>
                                                    )}
                                                </p>
                                                {appt.client_phone && (
                                                    <p className="text-xs text-gray-400 flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {formatPhoneNumber(appt.client_phone)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {upcoming && (
                                                <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-700 uppercase">
                                                    Upcoming
                                                </span>
                                            )}
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appt.chain_status)}`}>
                                                {(appt.chain_status || '').replace('_', ' ')}
                                            </span>
                                            {appt.location_id && (
                                                <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded">
                                                    <MapPin className="w-3 h-3" />
                                                    {getLocationName(appt.location_id)}
                                                </span>
                                            )}
                                            {/* Call client button */}
                                            {onCallClient && appt.client_phone && (
                                                <button
                                                    onClick={() => onCallClient(appt.client_phone, {
                                                        clientId: appt.client_id,
                                                        clientName: appt.client_name,
                                                        locationId: appt.location_id,
                                                    })}
                                                    className="p-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                                                    title="Call client"
                                                >
                                                    <PhoneOutgoing className="w-4 h-4" />
                                                </button>
                                            )}
                                            {/* Settings / detail button */}
                                            {onOpenChainDetail && (
                                                <button
                                                    onClick={() => onOpenChainDetail(toChainObj(appt))}
                                                    className="p-1.5 bg-transparent border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 text-gray-400 hover:text-gray-600 transition-colors"
                                                    title="View details"
                                                >
                                                    <Settings className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Two-column date display */}
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div className={`rounded-lg p-3 ${upcoming ? 'bg-emerald-50 border border-emerald-100' : 'bg-blue-50 border border-blue-100'}`}>
                                            <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${upcoming ? 'text-emerald-500' : 'text-blue-500'}`}>
                                                Appointment Date
                                            </p>
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className={`w-3.5 h-3.5 shrink-0 ${upcoming ? 'text-emerald-600' : 'text-blue-600'}`} />
                                                <p className={`text-sm font-semibold ${upcoming ? 'text-emerald-800' : 'text-blue-800'}`}>
                                                    {appointmentDate ? formatApptDate(appointmentDate) : (parsed.appointmentDateStr || '—')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                                                Booked On
                                            </p>
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                <p className="text-sm font-medium text-gray-600">
                                                    {formatCreatedDate(appt.booking_created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Appointment details */}
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                                        {parsed.pet && (
                                            <span className="flex items-center gap-1">
                                                🐕 <span className="font-medium">{parsed.pet}</span>
                                            </span>
                                        )}
                                        {parsed.groomer && (
                                            <span className="flex items-center gap-1">
                                                <User className="w-3 h-3 text-gray-400" />
                                                <span className="font-medium">{parsed.groomer}</span>
                                            </span>
                                        )}
                                        {parsed.service && (
                                            <span className="flex items-center gap-1">
                                                ✂️ {parsed.service}
                                            </span>
                                        )}
                                        {parsed.price && (
                                            <span className="font-medium text-gray-700">{parsed.price}</span>
                                        )}
                                        {parsed.confirmation && (
                                            <span className="text-gray-400">
                                                Conf #{parsed.confirmation}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Expand toggle bar */}
                                <button
                                    onClick={() => toggleExpand(cardKey)}
                                    className="w-full flex items-center justify-center gap-1 py-2 border-t bg-gray-50 hover:bg-gray-100 transition-colors text-xs text-gray-500 hover:text-gray-700"
                                >
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    {isExpanded ? 'Hide' : 'Show'} Communication Chain
                                </button>

                                {/* Expanded: Chain timeline + actions */}
                                {isExpanded && (
                                    <div className="border-t bg-gray-50 p-4">
                                        {/* Notes */}
                                        {appt.chain_notes && (
                                            <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-sm">
                                                <strong>Notes:</strong> {appt.chain_notes}
                                            </div>
                                        )}

                                        {/* Events timeline */}
                                        <div className="mb-4">
                                            <h4 className="font-medium mb-2">Timeline</h4>
                                            {appt.events && appt.events.length > 0 ? (
                                                <div className="space-y-2">
                                                    {getEventsNewestFirst(appt.events).map((event, eidx) => {
                                                        const info = getEventTypeInfo(event.type);
                                                        const isCallRecording = event.audio_bucket === 'groombot_conversations';
                                                        return (
                                                            <div key={eidx} className="flex items-start gap-3 text-sm">
                                                                <span className="text-lg">{info.icon}</span>
                                                                <div className="flex-1 min-w-0">
                                                                    <span className={`font-medium ${info.color}`}>
                                                                        {info.label}
                                                                    </span>
                                                                    {event.user_id && userIdToFullName[event.user_id] && (
                                                                        <span className="text-gray-500"> - {userIdToFullName[event.user_id]}</span>
                                                                    )}
                                                                    {event.notes && (
                                                                        event.notes.includes('\n') ? (
                                                                            <div className="mt-1 text-gray-600 text-xs whitespace-pre-wrap bg-white border rounded p-2">
                                                                                {event.notes}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-gray-600"> - {event.notes}</span>
                                                                        )
                                                                    )}
                                                                    <div className="text-xs text-gray-400">
                                                                        {fmtTs(event.timestamp)}
                                                                    </div>
                                                                    {event.audio_blob_name && (
                                                                        <AudioPlayer
                                                                            blobName={event.audio_blob_name}
                                                                            bucket={event.audio_bucket}
                                                                            label={isCallRecording ? 'Play call recording' : 'Play voicemail'}
                                                                        />
                                                                    )}
                                                                    {event.transcript && (
                                                                        <details className="mt-1">
                                                                            <summary className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                                                                                Show transcript
                                                                            </summary>
                                                                            <div className="mt-1 p-2 bg-white rounded border text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">
                                                                                {event.transcript}
                                                                            </div>
                                                                        </details>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 text-sm">No events yet</p>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex gap-2 flex-wrap">
                                            {onAddEvent && (
                                                <button
                                                    onClick={() => onAddEvent(appt.chain_id)}
                                                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                                                >
                                                    <Plus className="w-4 h-4 inline mr-1" />
                                                    Add Event
                                                </button>
                                            )}
                                            {onMarkResolved && appt.chain_status !== COMMUNICATION_CHAIN_STATUS.RESOLVED && (
                                                <button
                                                    onClick={() => onMarkResolved(appt.chain_id)}
                                                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                                                >
                                                    <CheckCircle className="w-4 h-4 inline mr-1" />
                                                    ✓ Mark Resolved
                                                </button>
                                            )}
                                            {onUpdateStatus && appt.chain_status === COMMUNICATION_CHAIN_STATUS.RESOLVED && (
                                                <button
                                                    onClick={() => onUpdateStatus(appt.chain_id, COMMUNICATION_CHAIN_STATUS.OPEN)}
                                                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700"
                                                >
                                                    Reopen
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ZoomBotAppointmentsPanel;
